import Add16 from '@carbon/icons-react/es/add/16';
import { useBoolean } from 'ahooks';
import { FormListOperation } from 'antd/es/form/FormList';
import { isEmpty, pick } from 'lodash';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import {
  CreateBranchingDetailsProps,
  FormChoiceOption,
  FormGroupColor,
  FormQuestion,
  FormQuestionType,
} from 'api/form/types';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Label } from 'components/atoms/label';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { Switch } from 'components/atoms/switch/switch';
import styles from 'components/molecules/forms/form-fields/rte.module.css';
import { useTranslation } from 'hooks/use-translation';
import { useFieldsQueryFilter } from 'queries';
import {
  useCreateFieldOptionMutation,
  useUpdateFieldOptionPositionMutation,
  useUpdateFieldQuestionMutation,
} from 'queries/form-template';
import { queueMacrotask } from 'utils/macrotask';
import { pascalCase } from 'utils/pascal-case';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import {
  ChoiceQuestionType,
  DropdownQuestionType,
  FormQuestionsList,
} from '../constants';
import { isFormDirty, isFormValid } from '../utils';
import { AssociatedFieldInfo } from './associated-project-field-info';
import { PhoneCountryCodeSelection } from './country-code-field';
import { getArbitraryColor } from './options/color-dropdown';
import { SortableOptions } from './options/sortable-options';
import { StarRatingConfigField } from './star-rating-config-field';

export type Values = Pick<
  FormQuestion,
  | 'questionId'
  | 'required'
  | 'questionText'
  | 'description'
  | 'options'
  | 'placeholder'
  | 'questionMeta'
>;

const StyledForm = styled(Form)`
  height: 100%;
  overflow: scroll;
  padding-bottom: var(--spacing-11);

  ${Form.Item} {
    margin-bottom: 0px;
  }
`;
const StyledCurrencyField = styled(Input)`
  padding-bottom: 0px;
`;

const PrimaryFormSection = styled.div`
  padding: var(--layout-02) var(--layout-01);
  border-bottom: 1px solid var(--border-color);

  && > .ant-form-item:not(:last-child) {
    padding-bottom: var(--layout-03);
  }

  && > .ant-form-item:last-child {
    padding-bottom: 0px;
  }
`;
const SecondaryFormSection = styled.div`
  padding: var(--layout-02) var(--layout-01);

  && > .ant-form-item:not(:last-child) {
    padding-bottom: var(--layout-03);
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-03);

  ${Label} {
    font-size: var(--font-size-base);
    margin-bottom: 0px;
  }
`;

const RequiredWrapper = styled(Form.Item)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-direction: row;

  .ant-form-item-control {
    flex-grow: 0;
  }

  .ant-form-item-label > label {
    font-size: var(--font-size-base);
  }
`;
const AddOptionButton = styled(Button)`
  > .ant-btn-loading-icon {
    padding-right: var(--spacing-03);
  }
`;
const FormQuestionIconGroup = styled.div`
  display: flex;
  user-select: none;
  align-items: center;
  margin-bottom: var(--layout-02);
`;
const FormQuestionIcon = styled.div<{ backgroundColor: string }>`
  padding: var(--spacing-02);
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-right: var(--spacing-03);
  background: ${({ backgroundColor }) => backgroundColor};
`;
const formFieldsList = {
  formQuestionType: 'formQuestionType',
  questionText: 'questionText',
  description: 'description',
  required: 'required',
  // using questionMeta here as this needs to be sent during update
  questionMeta: 'questionMeta',
};

const taskField = (field) => field.objectType === FieldSectionType.Task;
const isEnabled = (field) => field.isEnabled;
const taskFieldsFilter = [taskField, isEnabled];

export const FormFieldConfiguration: FC<{
  selectedField: FormQuestion;
  templateId: number;
  setCreateBranchingDetails: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
}> = ({ selectedField, templateId, setCreateBranchingDetails }) => {
  const [form] = Form.useForm();
  const [rte, setRte] = useState<RichTextEditor | null>(null);

  /**
   * Component state used to trigger event handlers
   * since RTE is caching the event handler
   * due to which state/ data objects are cached resulting in unexpected behaviour.
   */
  const [rteEvent, setRteEvent] = useState<string | null>(null);

  const [
    showDescription,
    { setTrue: setShowDescription, setFalse: setHideDescription },
  ] = useBoolean(!isEmpty(selectedField?.description));

  useEffect(() => {
    if (selectedField) {
      form.setFieldsValue({
        ...selectedField,
        description: selectedField.description ? selectedField.description : '',
      });
      if (selectedField.description) {
        setShowDescription();
      } else {
        setHideDescription();
      }
      queueMacrotask(() => rte?.focusAtEnd());
    }
  }, [selectedField, form, setShowDescription, setHideDescription, rte]);

  const requiredMessage = useTranslation('input.required');
  const currencySymbolPlaceholder = useTranslation(
    'form_templates.currency_symbol.placeholder',
  );

  const isChoicesSupported = selectedField
    ? ChoiceQuestionType.includes(selectedField.formQuestionType)
    : false;

  const isOptionColorSupported = selectedField
    ? DropdownQuestionType.includes(selectedField.formQuestionType)
    : false;

  const canShowRequiredField = ![FormQuestionType.TextStatement].includes(
    selectedField.formQuestionType,
  );
  const { data: projectCustomFields } = useProjectCustomFields();

  const { data: taskFields = [] } = useFieldsQueryFilter(taskFieldsFilter);

  const associatedProjectField = useMemo(() => {
    return projectCustomFields.find(
      (field) => field.fieldId === selectedField.projectFieldId,
    );
  }, [selectedField.projectFieldId, projectCustomFields]);

  const associatedTaskField = useMemo(() => {
    return taskFields.find(
      (field) => field.fieldId === selectedField.taskFieldId,
    );
  }, [selectedField.taskFieldId, taskFields]);

  const hasAssociatedField =
    !isEmpty(associatedProjectField) || !isEmpty(associatedTaskField);

  const { mutate: createOptionMutation, isLoading: isCreateOptionLoading } =
    useCreateFieldOptionMutation(templateId);

  const { mutate: updateFormQuestionMutation } =
    useUpdateFieldQuestionMutation(templateId);

  const { mutate: updateFormOptionPositionMutation } =
    useUpdateFieldOptionPositionMutation(templateId);
  const triggerOnChange = useCallback(() => {
    const formValues = form.getFieldValue([]);

    if (
      isFormValid(form) &&
      isFormDirty(formValues, selectedField, Object.values(formFieldsList))
    ) {
      const fieldConfiguration = pick(
        formValues,
        Object.values(formFieldsList),
      );

      updateFormQuestionMutation({
        questionId: selectedField.questionId,
        payload: fieldConfiguration,
      });
    }
  }, [form, selectedField, updateFormQuestionMutation]);

  const formQuestionIconMap = useMemo(() => {
    const questionItem = FormQuestionsList.find((formQuestion) => {
      return formQuestion.value === selectedField.formQuestionType;
    });
    const iconBackgroundColor = FormGroupColor[pascalCase(questionItem!.group)];

    return {
      ...questionItem,
      iconBackgroundColor,
    };
  }, [selectedField.formQuestionType]);

  const isQuestionMetaSupportedField = [
    FormQuestionType.Phone,
    FormQuestionType.Currency,
    FormQuestionType.StarRating,
  ].includes(selectedField.formQuestionType);

  useEffect(() => {
    switch (rteEvent) {
      case 'blur':
        triggerOnChange();
        break;
      default:
        break;
    }
    setRteEvent(null);
  }, [rteEvent, triggerOnChange]);

  return (
    <StyledForm
      form={form}
      name="form_field_configuration"
      autoComplete="off"
      initialValues={selectedField}
      preserve={false}
      layout="vertical"
    >
      {() => (
        <>
          <PrimaryFormSection>
            <FormQuestionIconGroup>
              <FormQuestionIcon
                backgroundColor={formQuestionIconMap.iconBackgroundColor}
              >
                {formQuestionIconMap.icon}
              </FormQuestionIcon>
              <FormattedMessage
                id={`form_templates.question.group.item.${formQuestionIconMap.label}`}
              />
            </FormQuestionIconGroup>
            <Form.Item name="questionId" hidden />
            <Form.Item
              label={
                <FormattedMessage id="form_templates.field_configuration.field_label" />
              }
              rules={[{ required: true, message: requiredMessage }]}
              shouldUpdate={(prev, current) =>
                prev[formFieldsList.questionText] !==
                current[formFieldsList.questionText]
              }
            >
              {({ getFieldValue, setFieldsValue }) => {
                const value = getFieldValue(formFieldsList.questionText);

                return (
                  <RichTextEditor
                    autoFocus
                    disallowNewLine
                    ref={(el) => setRte(el)}
                    className={styles['form-text']}
                    data={value ?? ''}
                    onChange={(_data) =>
                      setFieldsValue({
                        [formFieldsList.questionText]: _data,
                      })
                    }
                    onBlur={() => setRteEvent('blur')}
                    allowSimpleVariables
                  />
                );
              }}
            </Form.Item>

            {associatedProjectField && (
              <AssociatedFieldInfo
                fieldName={
                  associatedProjectField.fieldLabel ||
                  associatedProjectField.fieldName
                }
                fieldType="PROJECT_FIELD"
                fieldId={associatedProjectField.fieldId}
              />
            )}

            {associatedTaskField && (
              <AssociatedFieldInfo
                fieldName={
                  associatedTaskField.fieldLabel ||
                  associatedTaskField.fieldName
                }
                fieldType="TASK_FIELD"
                fieldId={associatedTaskField.fieldId}
              />
            )}

            {isChoicesSupported ? (
              <div>
                <Label>
                  <FormattedMessage id="choices" />
                </Label>
                <Form.List name="options">
                  {(fields, { add, remove, move }: FormListOperation) => {
                    const onSortEnd = ({ oldIndex, newIndex }) => {
                      move(oldIndex, newIndex);
                      const { options } = form.getFieldsValue(['options']);

                      updateFormOptionPositionMutation({
                        questionId: selectedField.questionId,
                        optionId: options[newIndex].optionId,
                        updatedOptionsList: options.map(
                          (option: FormChoiceOption, index: number) => {
                            return { ...option, position: index + 1 };
                          },
                        ),
                      });
                    };

                    return (
                      <>
                        <SortableOptions
                          useDragHandle
                          form={form}
                          fields={fields}
                          remove={remove}
                          onSortEnd={onSortEnd}
                          isOptionColorSupported={isOptionColorSupported}
                          templateId={templateId}
                          questionId={selectedField.questionId}
                          questionDepth={selectedField.depth || 0}
                          setCreateBranchingDetails={setCreateBranchingDetails}
                          hasAssociatedField={hasAssociatedField}
                        />

                        {!hasAssociatedField && (
                          <Form.Item>
                            <AddOptionButton
                              type="dashed"
                              onClick={() => {
                                createOptionMutation(
                                  {
                                    optionLabel: `Option ${fields.length + 1}`,
                                    questionId: selectedField.questionId,
                                    color: isOptionColorSupported
                                      ? getArbitraryColor(fields.length)
                                      : undefined,
                                  },
                                  {
                                    onSuccess: (formOption) => {
                                      add(formOption);
                                    },
                                  },
                                );
                              }}
                              block
                              loading={isCreateOptionLoading}
                              icon={<Add16 />}
                              data-cy="forms-choices-add.option"
                            >
                              <FormattedMessage id="settings.fields.add_option" />
                            </AddOptionButton>
                          </Form.Item>
                        )}
                      </>
                    );
                  }}
                </Form.List>
              </div>
            ) : null}

            {isQuestionMetaSupportedField && selectedField.questionMeta ? (
              <>
                <Form.Item name={['questionMeta', 'type']} hidden />
                <Form.Item
                  name={['questionMeta', 'countryCode']}
                  label={
                    <FormattedMessage id="form_templates.default_country_code" />
                  }
                  rules={[
                    {
                      required: true,
                      message: requiredMessage,
                    },
                  ]}
                  hidden={
                    selectedField.formQuestionType !== FormQuestionType.Phone
                  }
                >
                  <PhoneCountryCodeSelection
                    form={form}
                    initialValue={selectedField.questionMeta.countryCode || 0}
                    triggerOnChange={triggerOnChange}
                  />
                </Form.Item>
                <Form.Item
                  name={['questionMeta', 'currencySymbol']}
                  label={
                    <FormattedMessage id="form_templates.currency_symbol" />
                  }
                  rules={[
                    {
                      required: true,
                      message: requiredMessage,
                    },
                  ]}
                  hidden={
                    selectedField.formQuestionType !== FormQuestionType.Currency
                  }
                >
                  <StyledCurrencyField
                    maxLength={1}
                    placeholder={currencySymbolPlaceholder}
                    value={selectedField.questionMeta.currencySymbol || ''}
                    onBlur={() => triggerOnChange()}
                  />
                </Form.Item>
                <Form.Item
                  name={['questionMeta', 'numberOfStar']}
                  label={<FormattedMessage id="form_templates.no_of_stars" />}
                  rules={[
                    {
                      required: true,
                      message: requiredMessage,
                    },
                  ]}
                  hidden={
                    selectedField.formQuestionType !==
                      FormQuestionType.StarRating ||
                    (selectedField.projectFieldId !== null &&
                      selectedField.projectFieldId !== undefined)
                  }
                >
                  <StarRatingConfigField
                    form={form}
                    initialValue={selectedField.questionMeta.numberOfStar || 0}
                    triggerOnChange={triggerOnChange}
                  />
                </Form.Item>
              </>
            ) : null}
          </PrimaryFormSection>
          <SecondaryFormSection>
            <DescriptionWrapper>
              <Label>
                <FormattedMessage id="description" />
              </Label>
              <Switch
                size="small"
                checked={showDescription}
                onChange={(value) => {
                  if (value) {
                    setShowDescription();
                  } else {
                    setHideDescription();
                    form.setFieldsValue({
                      description: '',
                    });
                    triggerOnChange();
                  }
                }}
              />
            </DescriptionWrapper>
            <Form.Item
              hidden={!showDescription}
              shouldUpdate={(prev, current) =>
                prev[formFieldsList.description] !==
                current[formFieldsList.description]
              }
            >
              {({ getFieldValue, setFieldsValue }) => {
                const value = getFieldValue(formFieldsList.description);

                return (
                  <RichTextEditor
                    disallowNewLine
                    className={styles['form-text']}
                    data={value ?? ''}
                    onChange={(_data) =>
                      setFieldsValue({
                        [formFieldsList.description]: _data,
                      })
                    }
                    onBlur={() => setRteEvent('blur')}
                    allowSimpleVariables
                  />
                );
              }}
            </Form.Item>

            {canShowRequiredField && (
              <RequiredWrapper
                name={formFieldsList.required}
                valuePropName="checked"
                label={<FormattedMessage id="required" />}
              >
                <Switch size="small" onChange={() => triggerOnChange()} />
              </RequiredWrapper>
            )}
          </SecondaryFormSection>
        </>
      )}
    </StyledForm>
  );
};
